import { SelectionModel } from '@angular/cdk/collections';
import * as Highcharts from 'highcharts';
import {
  Component,
  ViewChild,
  OnInit,
  AfterViewInit,
  inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatAccordion } from '@angular/material/expansion';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { PaginationService } from '../../../helpers/pagination.service';
import { AccountingService } from '../accounting.service';
import { contactsDataGridColumns } from '../models/account-column-config.model';
import {
  AccountRequestType,
  AccountResponseType,
  IAccount,
} from '../models/account.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CommonService } from '../../../helpers/common.service';
import { DateConverterService } from '../../../helpers/date-converter.service';

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../accounting-table.scss', './account.component.scss'],
})
export class AccountComponent implements OnInit, AfterViewInit {
  @ViewChild(MatAccordion) accordion!: MatAccordion;

  visibleFieldKeys: string[] = [
    'select',
    'profileCreationDate',
    'profileId',
    'isClient',
    'firstName',
    'fullAddress',
    'phone',
    'email',
    'status',
    'actions',
  ];
  data: IAccount[] = [];
  selection = new SelectionModel<any>(true, []);
  pageSize: number = 50;
  pageIndex: number = 0;
  pageOffset: number = 0;
  columns = contactsDataGridColumns;
  displayedColumns: string[] = [];
  dataSource = new MatTableDataSource<any>([]);

  amount: number = 40;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  sortedData: IAccount[] = [];
  readonly dialog = inject(MatDialog);

  @ViewChild('bookingPicker') bookingPicker!: MatDateRangePicker<Date>;
  minDate!: Date;
  maxDate!: Date;
  startDate!: Date;
  endDate!: Date;
  sortColumns: string = 'id';
  sortOrder: string = 'desc';
  mode: number = 1;
  searchQuery: string = '';
  totalResults: number = 0;

  constructor(
    private _accountingService: AccountingService,
    private paginationService: PaginationService,
    private _snackBar: MatSnackBar,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private _commonService: CommonService,
    private dateConversionService: DateConverterService,
  ) {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const currentDate = new Date().getDate();
    this.minDate = new Date(2022, 9, 93);
    this.maxDate = new Date(currentYear, currentMonth, currentDate);
  }

  openDatePicker(): void {
    this.bookingPicker.open();
  }

  ngOnInit(): void {
    this.initializeDefaultBookingDates.call(this);
    this.contactsData(
      this.pageOffset,
      this.pageIndex,
      this.sortColumns,
      this.sortOrder,
      '2025-08-14T12:49:34.282Z',
      '2025-08-14T12:49:34.282Z',
      this.searchQuery,
      this.mode,
    );
  }

  initializeDefaultBookingDates() {
    this.startDate = new Date(new Date().getFullYear(), 0, 1); // Start: Jan 1st of current year
    this.endDate = new Date(); // End: today
  }

  contactsData(
    pageOffset: number,
    pageSize: number,
    sortColumn: string,
    sortOrder: string,
    startDate?: string,
    endDate?: string,
    searchQuery?: string,
    mode?: number,
  ) {
    const params: AccountRequestType = {
      startDate: '2025-08-14T12:49:34.282Z',
      endDate: '2025-08-14T12:49:34.282Z',
      pageOffset: this.pageOffset,
      pageSize: this.pageSize,
      sortOrder: sortOrder,
      columnName: sortColumn,
      searchQuery: searchQuery,
      mode: mode,
    };
    this._accountingService.getContactsData(params).subscribe({
      next: (res: AccountResponseType) => {
        this.data = res.value.profileInfoDetails;
        this.totalResults = res.value.profilesCount;

        if (!res.isError) {
          // this._snackBar.open('Contacts fetched successfully', 'Close', {
          //   duration: 3000,
          //   horizontalPosition: 'center',
          //   verticalPosition: 'top',
          // });
          this.dataSource.data = [...this.dataSource.data, ...this.data];

          this.sortedData = this.dataSource.data.slice(this.pageOffset);
          this.cdr.detectChanges();
        }
      },
      error: () => {
        this._snackBar.open('Unable to get the import invoices', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
    });
  }

  handleSortChange(event: Sort) {
    this.dataSource.data = [];
    // this.resultsLen = 0;
    if (event.active && event.direction) {
      this.contactsData(
        this.amount,
        this.pageIndex,
        event.active,
        event.direction,
        '2025-08-14T12:49:34.282Z',
        '2025-08-14T12:49:34.282Z',
        this.searchQuery,
        this.mode,
      );
    } else {
      //TODO remove after testing
      // this._snackBar.open("Sorting is been cleared", "Close", {
      //   duration: 3000,
      //   horizontalPosition: "center",
      //   verticalPosition: "top",
      // });
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    // this.paginator.length = this.dataSource.data.length;

    this.sortedData = this.dataSource.data.slice(startIndex, endIndex);
    // this.sortCurrentPageData(); // Apply sorting after fetching data
    this.updateDisplayedColumns();

    this.sort.sortChange.subscribe(() => {
      this.sortCurrentPageData();
    });
  }

  updateDisplayedColumns() {
    this.displayedColumns = this.visibleFieldKeys;
  }

  toggleColumnVisibility(key: string) {
    if (this.visibleFieldKeys.includes(key)) {
      // If the key is already visible, remove it.
      this.visibleFieldKeys = this.visibleFieldKeys.filter((k) => k !== key);
    } else {
      // Find the original index of the key in the full columns array.
      const originalIndex = this.columns.findIndex((col) => col.key === key);

      // Find the correct insertion point in the visibleFieldKeys array.
      let insertIndex = this.visibleFieldKeys.length; // Default to end

      for (let i = 0; i < this.visibleFieldKeys.length; i++) {
        const visibleKey = this.visibleFieldKeys[i];
        // Find the original index of the currently visible key
        const visibleOriginalIndex = this.columns.findIndex(
          (col) => col.key === visibleKey,
        );

        // If the new key's original index is less than the current visible key's original index,
        // it means we've found our insertion point.
        if (originalIndex < visibleOriginalIndex) {
          insertIndex = i;
          break; // Stop searching once the position is found
        }
      }

      // Insert the new key at the determined index.
      this.visibleFieldKeys.splice(insertIndex, 0, key);
    }

    // Update the displayed columns to reflect the change.
    this.updateDisplayedColumns();
  }

  sortCurrentPageData() {
    if (this.sort.active && this.sort.direction !== '') {
      const startIndex = this.pageIndex * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      // this.paginator.length = this.dataSource.data.length;

      //   const currentPageData = this.dataSource.data.slice(startIndex, endIndex);
      //   this.sortedData = currentPageData.sort((a, b) => {
      //     const isAsc = this.sort.direction === 'asc';
      //     const isDesc = this.sort.direction === 'desc';
      //     switch (this.sort.active) {
      //       case 'Id':
      //         return this.compare(a.Id, b.Id, isAsc);
      //       case 'Stripe_Id':
      //         return this.compare(a.Stripe_Id, b.Stripe_Id, isAsc);
      //       case 'Payment_Type':
      //         return this.compare(a.Payment_Type, b.Payment_Type, isAsc);
      //       case 'Payment_Source':
      //         return this.compare(a.Payment_Source, b.Payment_Source, isAsc);
      //       case 'Created_on':
      //         return this.compare(a.Created_on, b.Created_on, isAsc);
      //       case 'Available_on':
      //         return this.compare(a.Available_on, b.Available_on, isAsc);
      //       case 'Amount':
      //         return this.compare(a.Amount, b.Amount, isAsc);
      //       case 'Created_date':
      //       case 'Modified_date':
      //       case 'Transfer_Date':
      //         return this.sortingDate(
      //           this.parseDate(a.executionDate),
      //           this.parseDate(b.executionDate),
      //           isDesc,
      //         );
      //       default:
      //         return 0;
      //     }
      //   });
    }
  }

  // Custom comparison function
  compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  sortingDate(
    a: number | string | Date,
    b: number | string | Date,
    isAsc: boolean,
  ) {
    // Convert string dates to Date objects if they are not already
    const dateA = new Date(a);
    const dateB = new Date(b);

    // Compare the dates
    return (dateA.getTime() - dateB.getTime()) * (isAsc ? 1 : -1);
  }

  parseDate(dateString: string): Date {
    const [day, month, year] = dateString.split('-').map(Number); // Split the string and convert to numbers
    return new Date(year, month - 1, day); // Create a Date object (month is zero-based)
  }

  onPageChange($event: PageEvent) {
    this.pageIndex = $event.pageIndex;
    this.pageSize = $event.pageSize;

    this.pageOffset = $event.pageIndex * this.pageSize; // Calculate page offset for API calls

    this.contactsData(
      this.pageOffset,
      this.pageIndex,
      this.sortColumns,
      this.sortOrder,
      '2025-08-14T12:49:34.282Z',
      '2025-08-14T12:49:34.282Z',
      this.searchQuery,
      this.mode,
    );
    this.cdr.detectChanges();

    const offsetReached = ($event.pageIndex + 1) * $event.pageSize;
    const isNearEnd = offsetReached >= this.dataSource.data.length;
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
  }

  searchAccountQuery(event: any): void {
    const searchValue = event.target.value.toLowerCase();
    this.dataSource.filter = searchValue.trim().toLowerCase();
    this.sortedData = this.dataSource.filteredData.slice(0, this.pageSize);
  }

  // Logic to delete account
  deleteAccount(row: IAccount) {
    this._accountingService.deleteContact(row.profileId).subscribe({
      next: (res: number) => {
        this._snackBar.open('Account deleted successfully', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
        this.dataSource.data = this.dataSource.data.filter(
          (item) => item.profileId !== row.profileId,
        );
        this.sortedData = this.dataSource.data.slice(0, this.pageSize);
      },
      error: (error) => {
        this._snackBar.open('Failed to delete account', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
    });
  }

  importData(ids: any) {
    const selectedIds = ids?.selected.map((row: any) => row.id);
    this._accountingService.importAccountingData(selectedIds).subscribe({
      next: (res: number[]) => {
        this._snackBar.open('Import successful', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
        this.dataSource.data = this.dataSource.data.filter(
          (item) => !selectedIds.includes(item.id),
        );
        this.sortedData = this.dataSource.data.slice(0, this.pageSize);
        this.selection.clear();
      },
      error: (error) => {
        this._snackBar.open('Failed to import invoices', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
    });
  }

  // exportData() {
  //   // Logic to export data
  //   this._snackBar.open('Export functionality is not implemented yet', 'Close', {
  //     duration: 3000,
  //     horizontalPosition: 'center',
  //     verticalPosition: 'top',
  //   });
  // }

  openAccountDetails(rowData: IAccount) {
    const accountId = rowData?.id;
    // sessionStorage.setItem('currentNavName', accountId);
    sessionStorage.setItem('accountData', JSON.stringify(rowData));
    this.router.navigate([
      '/klusup/accounting/account/account-details',
      accountId,
    ]);
  }

  Highcharts: typeof Highcharts = Highcharts;
  barChartOptions: Highcharts.Options = {};
  lineChartOptions: Highcharts.Options = {};

  barChart() {
    this.barChartOptions = {
      chart: {
        type: 'bar',
      },
      title: {
        text: 'Import Data Chart',
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
          },
        },
      },
      xAxis: {
        title: {
          text: 'Date',
        },
        categories: this.sortedData.map((item) => item.creationDate),
      },
      yAxis: {
        title: {
          text: 'statusDesc',
        },
      },
      series: [
        {
          name: 'Status Import',
          type: 'bar',
          data: this.sortedData.map((item) => item.profileId),
        },
      ],
    };
  }

  private prepareChartData(): void {
    const monthlyStatusData: Record<string, { sum: number; count: number }> =
      {};

    // Initialize all months to ensure all categories are present, even if no data
    this._commonService.monthNames.forEach((month) => {
      monthlyStatusData[month] = { sum: 0, count: 0 };
    });

    // Aggregate data: Group by month and sum the numerical status values
    this.sortedData.forEach((item) => {
      // Use 'profileCreationDate' as per your requirement
      const month = this._commonService.getMonthFromDate(
        item.profileCreationDate,
      );
      // Get the numerical value for the status, default to 0 if not mapped
      const statusValue =
        this._commonService.statusToAccountsMapping[item.statusDesc];

      monthlyStatusData[month].sum += statusValue;
      monthlyStatusData[month].count += 1;
    });

    // Calculate the average status value for each month for the chart series
    const chartSeriesData: (number | null)[] =
      this._commonService.monthNames.map((month) => {
        const { sum, count } = monthlyStatusData[month];
        // parseFloat((sum / count).toFixed(2))
        // Return the average, or null if no data for that month (chart will draw a gap)
        return count > 0 ? count : 0;
      });

    // Set your Highcharts options
    this.lineChartOptions = {
      chart: {
        type: 'line',
      },
      title: {
        text: 'Account Profile Status Trend',
      },
      subtitle: {
        text: 'Average Profile Status Value per Month (e.g., Rejected=0, Pending=1, Open=2, Closed=3)',
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
            format: '{point.y:.2f}', // Display numerical average with 2 decimal places
          },
          showInLegend: true,
          marker: {
            enabled: true, // Show points on the line
          },
        },
      },
      xAxis: {
        title: {
          text: 'Month',
        },
        categories: this._commonService.monthNames, // Use the full list of months for categories
      },
      yAxis: {
        title: {
          text: 'Average Status Value',
        },
        min: 0, // Adjust based on your lowest mapped status value
        max: 20, // Adjust based on your highest mapped status value + a little buffer
        allowDecimals: true, // Allow decimal values for averages
      },
      series: [
        {
          name: 'Average Profile Status',
          type: 'line',
          data: chartSeriesData, // Use the processed numerical data
          tooltip: {
            valueDecimals: 2, // Show 2 decimals in tooltip
          },
        },
      ],
      credits: {
        enabled: false, // Hides "Highcharts.com" credit
      },
    };
  }
}
